import { AppNS } from 'app';
import PermitsInfo from 'app/main/common/bookings/permits-info/PermitsInfo';
import SwoInfo from 'app/main/common/bookings/swo-info/SwoInfo';
import ProjectAddressFormatted from 'app/ui/components/project-address-formatted/ProjectAddressFormatted';
import { getStatus } from 'app/ui/components/user-dashboard/helpers/common';
import { ExternalButton } from 'app/ui_v2/components/external-button/ExternalButton';

import { Box, Card, SxProps, Theme, useTheme } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import BookingSpecial from '../../../common/bookings/booking-special/BookingSpecial';
import { getProjectExternalLink } from '../../inspections/config';
import BookingActions from '../booking-actions/BookingActions';
import BookingCustomer from '../booking-customer/BookingCustomer';

export interface BookingsTableProps {
  data: AppNS.ProjectLead[];
}

const checkData = (data: Array<AppNS.ProjectLead>): boolean => {
  return data.some((item: any) => item?.type === 'special' || item?.type === 'doee');
};

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    table: {
      width: '100%',
      '& thead th': {
        padding: '16px 24px',
      },
      '& tbody td': {
        padding: '16px',
      },
      '& tbody td:first-of-type': {
        paddingLeft: '24px',
      },
      '& tbody tr.is-warning td': {
        background: '#fff6e5',
      },
      '& tbody td:last-child': {
        paddingRight: '24px',
      },
    },
    cellHelper: {
      fontSize: '1.2rem',
    },
    tableWrapper: {
      width: '100%',
      overflow: 'auto',
    },
    legacyText: {
      color: theme.palette.secondary.main,
      fontSize: '12px',
    },
  };
};

export default function BookingsTable(props: BookingsTableProps) {
  const { data } = props;

  const theme = useTheme();
  const styles = getStyles(theme);

  const dataHasSpecialEntry = checkData(data);

  return (
    <Card>
      <Box sx={styles.tableWrapper}>
        <Table sx={styles.table} aria-label="Events table">
          <TableHead>
            <TableRow>
              {dataHasSpecialEntry && <TableCell></TableCell>}
              <TableCell>
                <Box ml={dataHasSpecialEntry ? -1 : 0}>Project #</Box>
              </TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Permits</TableCell>
              <TableCell>Project Owner / Agency</TableCell>
              <TableCell>Where</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item: any) => (
              <TableRow key={item.id} className={item.job?.swoSetAt ? 'is-warning' : ''}>
                {dataHasSpecialEntry && (
                  <TableCell scope="row">
                    {item?.type === 'special' && <BookingSpecial />}
                    {item?.type === 'doee' && <BookingSpecial type="doee" tooltip="DOEE Inspection" />}
                  </TableCell>
                )}
                <TableCell scope="row">
                  <ExternalButton style={{ textDecoration: 'none' }} to={getProjectExternalLink(item.id)}>
                    <strong>{item.id}</strong>
                  </ExternalButton>
                  {item?.legacyProject && <Box sx={styles.legacyText}> Legacy Project </Box>}
                  {item?.swoSetAt && <SwoInfo type="warning" />}
                </TableCell>
                <TableCell>
                  <BookingCustomer client={item.client} />
                </TableCell>
                <TableCell>{item.permitNumbers && <PermitsInfo permits={item.permitNumbers} />}</TableCell>
                <TableCell>
                  {item?.booking && (
                    <>
                      <div>{item?.booking?.agency?.fullName}</div>
                      <div>{item?.booking?.agencyCompany?.name || '-'}</div>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <ProjectAddressFormatted address={item} />
                </TableCell>
                <TableCell>{getStatus(item.state)}</TableCell>
                <TableCell align="right">
                  <BookingActions id={item.id} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}
